import logger from '../utils/logger.js';
import { fetchSecretValue } from './graphApi.js';

const DEFAULT_MAP_CONFIG_SECRET_NAME = 'cubee2e-datacore-bedrock';
const MAX_BOOTSTRAP_RETRIES = 4;
const RETRY_WAIT_MS = 700;

const runtimeMapConfig = {
    googleMapsApiKey: '',
    googleMapId: ''
};

const pickFirstString = (valueMap, keys) => {
    for (const key of keys) {
        const value = valueMap?.[key];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }
    return '';
};

const normalizeSecretPayload = (secretValue) => {
    if (!secretValue) {
        return {};
    }

    if (typeof secretValue === 'string') {
        try {
            const parsed = JSON.parse(secretValue);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    }

    return typeof secretValue === 'object' ? secretValue : {};
};

const getMapConfigSecretName = () => {
    const globalSecretName = globalThis?.APP_RUNTIME_MAP_SECRET_NAME;
    if (typeof globalSecretName === 'string' && globalSecretName.trim()) {
        return globalSecretName.trim();
    }
    return DEFAULT_MAP_CONFIG_SECRET_NAME;
};

const setRuntimeMapConfig = ({ apiKey = '', mapId = '' } = {}) => {
    runtimeMapConfig.googleMapsApiKey = apiKey;
    runtimeMapConfig.googleMapId = mapId;

    // Keep existing global compatibility path for older callsites and tests.
    globalThis.GOOGLE_MAPS_API_KEY = apiKey;
    globalThis.GOOGLE_MAP_ID = mapId;
};

const extractMapConfig = (secretValue) => {
    const secret = normalizeSecretPayload(secretValue);

    const apiKey = pickFirstString(secret, ['GOOGLE_MAPS_API_KEY']);
    const mapId = pickFirstString(secret, ['GOOGLE_MAP_ID']);

    return { apiKey, mapId };
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isTransientAuthBootstrapError = (error) => {
    const message = String(error?.message || '').toLowerCase();
    const errorCode = String(error?.errorCode || error?.code || '').toLowerCase();

    return (
        errorCode === 'interaction_in_progress'
        || errorCode === 'auth_token_unavailable'
        || message.includes('interaction_in_progress')
        || message.includes('auth token unavailable')
    );
};

export const initializeRuntimeMapConfig = async () => {
    const secretName = getMapConfigSecretName();

    try {
        let payload;

        for (let attempt = 1; attempt <= MAX_BOOTSTRAP_RETRIES; attempt += 1) {
            try {
                payload = await fetchSecretValue(secretName);
                break;
            } catch (error) {
                const shouldRetry = isTransientAuthBootstrapError(error) && attempt < MAX_BOOTSTRAP_RETRIES;
                if (!shouldRetry) {
                    throw error;
                }
                await wait(RETRY_WAIT_MS * attempt);
            }
        }

        const { apiKey, mapId } = extractMapConfig(payload?.secretValue);
        setRuntimeMapConfig({ apiKey, mapId });
    } catch (error) {
        if (isTransientAuthBootstrapError(error)) {
            logger.info('[GeoMap] Runtime map config bootstrap deferred:', error?.message || error);
        } else {
            logger.warn('[GeoMap] Runtime map config bootstrap failed:', error?.message || error);
        }
        setRuntimeMapConfig({ apiKey: '', mapId: '' });
    }

    return {
        googleMapsApiKey: runtimeMapConfig.googleMapsApiKey,
        googleMapId: runtimeMapConfig.googleMapId
    };
};

export const getRuntimeGoogleMapsApiKey = () => runtimeMapConfig.googleMapsApiKey;

export const getRuntimeGoogleMapId = () => runtimeMapConfig.googleMapId;
